import { useMemo } from 'react';

import { Box, Link, Tooltip } from '@mui/material';
import { MRT_ColumnDef } from 'material-react-table';
import { Link as RouterLink } from 'react-router-dom';
import SubTitle from 'src/components/v2/SubTitle';
import OperationWalletTable from 'src/pages/Operation/OperationWalletTable';
import {
  MetaCarteirasFicFidcsChildPosition,
  MetaCarteirasFicFidcsChildrenMeta,
} from 'src/services/meta-carteiras-fic-fidcs';
import { convertValue, formatPercentageValue } from 'src/utils/number';

import FicFidcPositionsSummary from './FicFidcPositionsSummary';

interface FicFidcPositionsTableProps {
  positions: MetaCarteirasFicFidcsChildPosition[];
  meta?: MetaCarteirasFicFidcsChildrenMeta;
  isLoading: boolean;
  hasPermission: boolean;
}

export default function FicFidcPositionsTable({
  positions,
  meta,
  isLoading,
  hasPermission,
}: FicFidcPositionsTableProps) {
  const columns: MRT_ColumnDef<any>[] = useMemo(
    () => [
      {
        accessorKey: 'name',
        header: 'Fundo',
        Cell: ({ row }) => {
          const { name, slug, operationId } =
            row.original as MetaCarteirasFicFidcsChildPosition;
          if (!name) return '-';
          if (!slug || !operationId) return name;
          return (
            <Link
              component={RouterLink}
              to={`/operacao/fidc/${slug}/${operationId}`}>
              {name}
            </Link>
          );
        },
      },
      {
        accessorKey: 'cnpj',
        header: 'CNPJ',
        Cell: ({ cell }) => cell.getValue<string | null>() ?? '-',
      },
      {
        accessorKey: 'ativoLastro',
        header: 'Ativo Lastro',
        Cell: ({ cell }) => cell.getValue<string | null>() ?? '-',
      },
      {
        accessorKey: 'vlMercPosFinal',
        header: 'Posição em R$',
        accessorFn: (row: MetaCarteirasFicFidcsChildPosition) =>
          row.vlMercPosFinal ?? 0,
        Cell: ({ row }) =>
          row.original.vlMercPosFinal !== null
            ? convertValue(row.original.vlMercPosFinal)
            : '-',
      },
      {
        accessorKey: 'percentage',
        header: 'Percentual do PL',
        accessorFn: (row: MetaCarteirasFicFidcsChildPosition) =>
          row.percentage ?? 0,
        Cell: ({ row }) => {
          const percentage = row.original.percentage;

          if (percentage === null || percentage === undefined) return '-';

          const formattedPercentageTooltip = formatPercentageValue(
            percentage,
            4,
          );
          const formattedPercentage = formatPercentageValue(percentage);

          return (
            <Tooltip
              arrow
              placement="top"
              title={`O valor representa ${formattedPercentageTooltip} do patrimônio líquido do fundo.`}>
              <Box
                component="span"
                tabIndex={0}
                sx={{
                  cursor: 'help',
                  textDecoration: 'underline dotted',
                  textUnderlineOffset: '3px',
                }}>
                {formattedPercentage}
              </Box>
            </Tooltip>
          );
        },
      },
    ],
    [],
  );

  if (hasPermission && !isLoading && !positions.length) return null;

  return (
    <Box>
      <SubTitle title="— Composição da Carteira" />
      <OperationWalletTable
        hasPermission={hasPermission}
        data={positions}
        columns={columns}
        isLoading={isLoading}
        permission="operacoes_fidic_ativo_carteira"
        initialSorting={[{ id: 'vlMercPosFinal', desc: true }]}
        totalRows={meta?.total}
        baseMonth={meta?.baseMonth ?? positions[0]?.dtComptc}
        baseMonthFormat="MM/yyyy"
        topPadding={0}
        footerContent={
          meta?.totalVlMercPosFinal !== undefined &&
          meta.totalVlMercPosFinal !== null &&
          meta?.totalPercentage !== undefined &&
          meta.totalPercentage !== null ? (
            <FicFidcPositionsSummary
              totalVlMercPosFinal={meta.totalVlMercPosFinal}
              totalPercentage={meta.totalPercentage}
            />
          ) : undefined
        }
      />
    </Box>
  );
}
